import { DragEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, download, IMPORT_STATUS, ImportJob, uploadFile } from '../api';
import { ErrorBox, fmtDate, useAsync } from '../components/ui';

const TEMPLATES = [['company', 'Cégek'], ['contact', 'Kontaktok'], ['deal', 'Dealek'], ['task', 'Feladatok'], ['event', 'Események']];

/** Importok listája, feltöltés és sablonok (M9). */
export function ImportsPage() {
  const nav = useNavigate();
  const [items, setItems] = useState<ImportJob[] | null>(null);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const a = useAsync();
  const dl = useAsync();
  useEffect(() => { a.run(async () => setItems((await api<{ items: ImportJob[] }>('/imports')).items)); }, []);

  const send = async (f?: File | null) => {
    if (!f) return;
    const j = await a.run(() => uploadFile<ImportJob>('/imports', f));
    if (j) nav(`/import/${j.id}`);
  };
  const drop = (e: DragEvent) => { e.preventDefault(); setOver(false); send(e.dataTransfer.files?.[0]); };

  return (
    <div>
      <div className="page-head"><h2>Import</h2></div>
      <div className="cols">
        <div>
          <section className={`card dropzone ${over ? 'over' : ''}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}>
            <h3>Új import</h3>
            <p>Húzd ide az Excel (.xlsx) vagy CSV fájlt, vagy <button className="link inline" onClick={() => input.current?.click()} disabled={a.busy}>válaszd ki</button>.</p>
            <p className="muted small">Egy munkafüzet több lapja egyszerre importálható (pl. Cégek, Kontaktok, Dealek, Feladatok, Események) – a lapok egymásra hivatkozhatnak.
              Legfeljebb 50 MB és 200 000 sor. Feltöltés után még semmi nem kerül be: előbb átnézed a hozzárendelést és az előnézetet.</p>
            <input ref={input} type="file" accept=".xlsx,.csv,.txt" hidden onChange={(e) => { send(e.target.files?.[0]); e.target.value = ''; }} />
            {a.busy && <p className="muted">Feltöltés és beolvasás…</p>}
            <ErrorBox error={a.error} />
          </section>
          <section className="card">
            <h3>Korábbi importok</h3>
            <table className="list">
              <thead><tr><th>Fájl</th><th>Állapot</th><th className="num">Sor</th><th className="num">Új / frissült</th><th>Feltöltve</th></tr></thead>
              <tbody>
                {items?.map((j) => (
                  <tr key={j.id} onClick={() => nav(`/import/${j.id}`)}>
                    <td><Link to={`/import/${j.id}`}>{j.file_name}</Link></td>
                    <td><span className={`badge status-${j.status}`}>{IMPORT_STATUS[j.status] ?? j.status}</span></td>
                    <td className="num">{j.total_rows}</td>
                    <td className="num">{j.summary.create != null ? `${j.summary.create} / ${j.summary.update}` : '–'}</td>
                    <td>{fmtDate(j.created_at)}{j.created_by_name && <span className="muted"> · {j.created_by_name}</span>}</td>
                  </tr>
                ))}
                {items?.length === 0 && <tr><td colSpan={5} className="muted">Még nem volt import.</td></tr>}
              </tbody>
            </table>
          </section>
        </div>
        <div>
          <section className="card">
            <h3>Sablonok</h3>
            <p className="small">Ha még nincs kész fájlod, ebből indulj ki. A munkafüzet minden lapján van egy összefüggő példasor, az „Útmutató” lap leírja az oszlopokat.</p>
            <div className="actions">
              <button onClick={() => dl.run(() => download('/imports/templates/brunocrm-import-sablon.xlsx'))}>Excel-sablon (.xlsx)</button>
            </div>
            <p className="muted small">CSV típusonként: {TEMPLATES.map(([t, l], i) => (
              <span key={t}>{i > 0 && ' · '}<button className="link inline" onClick={() => dl.run(() => download(`/imports/templates/${t}.csv`))}>{l}</button></span>
            ))}</p>
            <ErrorBox error={dl.error} />
          </section>
          <section className="card">
            <h3>Hogyan párosít?</h3>
            <ul className="small tight">
              <li><strong>Cég</strong>: azonosító, majd adószám. <strong>Kontakt</strong>: azonosító, majd e-mail. <strong>Deal, feladat</strong>: azonosító.</li>
              <li>Egyezésnél a kitöltött mezők frissülnek, az üres cella semmit nem töröl (vagy beállíthatod, hogy a meglévőt hagyja ki).</li>
              <li>A historikus események (hívás, e-mail, jegyzet, megbeszélés, lépésváltás) az eredeti időpontjukra kerülnek, és nem indítanak ügynököt.</li>
              <li>Ugyanaz a fájl ismét futtatható – nem duplikál. Egy import 30 napig egészében visszavonható.</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
